import React from 'react';
import { cn } from '../../utils/cn';
import { AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';

const alertVariants = {
  default: 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700',
  destructive: 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800',
  success: 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800',
  warning: 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800',
  info: 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800',
};

const alertIcons = {
  default: AlertCircle,
  destructive: AlertCircle,
  success: CheckCircle,
  warning: AlertTriangle,
  info: Info,
};

const Alert = React.forwardRef(({
  className,
  variant = 'default',
  children,
  ...props
}, ref) => {
  const Icon = alertIcons[variant];
  
  const baseClasses = 'relative w-full rounded-lg border p-4';
  const variantClasses = alertVariants[variant];
  
  const classes = cn(
    baseClasses,
    variantClasses,
    className
  );

  return (
    <div
      ref={ref}
      role="alert"
      className={classes}
      {...props}
    >
      <div className="flex items-start">
        <Icon className="h-4 w-4 mt-0.5 mr-3 flex-shrink-0" />
        <div className="flex-1">
          {children}
        </div>
      </div>
    </div>
  );
});

Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef(({
  className,
  children,
  ...props
}, ref) => {
  return (
    <h5
      ref={ref}
      className={cn('mb-1 font-medium leading-none tracking-tight', className)}
      {...props}
    >
      {children}
    </h5>
  );
});

AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef(({
  className,
  children,
  ...props
}, ref) => {
  return (
    <div
      ref={ref}
      className={cn('text-sm [&_p]:leading-relaxed', className)}
      {...props}
    >
      {children}
    </div>
  );
});

AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription, alertVariants };
